import styles from './InstrumentStrip.module.css'
import { useT } from '../../../lib/i18n'

/**
 * The desk's instrument panel (desk-grammar brief §3).
 *
 * A readout exists only when its data does. Absence is rendered as nothing —
 * never a dash, never "—", never a zero standing in for a missing reading. A
 * placeholder pretending to be data is the one thing an instrument may not do.
 *
 * ⚠ [CONTRACT PENDING] — the server emits no telemetry today. DEPTH, HEART, GAS,
 * GEOPHONE, CHARGE and NODE therefore never appear in `routes/public/`; they are
 * exercised from the dev harness. NEXT SKED and the desk clock are live now,
 * sourced from session/arrangement data.
 */
export interface Readout {
  key: string
  /** Lowercase, small, letterspaced. The instrument's engraved name. */
  label: string
  /** The measurement. Set in the mono/tabular variant. */
  value: string
  /** True when `value` is a stated condition ("strap off"), not a measurement. */
  stated?: boolean
  /** A secondary note, e.g. a countdown beside an absolute time. */
  hint?: string
}

export default function InstrumentStrip({
  readouts,
}: {
  readouts: (Readout | null | undefined)[]
}) {
  const t = useT()
  const present = readouts.filter((r): r is Readout => Boolean(r))
  if (present.length === 0) return null

  return (
    <div className={styles.strip} role="status" aria-label={t('desk.instruments.aria')}>
      {present.map((r) => (
        <div
          key={r.key}
          className={`${styles.readout} ${r.key === 'clock' ? styles.clock : ''}`}
        >
          <div className={styles.label}>{r.label}</div>
          <div className={`${styles.value} ${r.stated ? styles.stated : ''}`}>
            {r.value}
            {r.hint && <span className={styles.hint}> · {r.hint}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}
